/* ======== UI ========= */
import { configureFonts } from "react-native-paper";

import type { DemoExamsType } from "@constants/demo";
import { buildType } from "@helpers/expo-extras";
import { useAppStore } from "@hooks/useAppStore";
/* ======== Helpers ========= */
import { typography } from "@memorang/helpers";
import merge from "lodash-es/merge";
import type { Apps } from "types/apps";
import { breakpoints, componentOverrides, fontConfig } from "./constants";
import { getCustomPalette, getMergedCustomTheme, textColors } from "./helpers";

const buildTheme = (
	appName: Apps,
	darkMode: boolean,
	demoExamId?: DemoExamsType,
) => {
	const appFont = useAppStore.getState().app.font;
	const customTextColors = textColors(darkMode);
	const customPalette = getCustomPalette(darkMode);
	const mergedCustomTheme = getMergedCustomTheme({
		appName,
		darkMode,
		demoExamId,
	});
	const customTypography = typography(darkMode, appFont ?? "opensans");
	const fonts = configureFonts({ config: fontConfig });
	const colors = {
		...merge(customPalette, mergedCustomTheme.colors),
		text: darkMode ? "#fff" : "#000",
		white: "#fff",
		black: "#000",
	};

	return {
		...mergedCustomTheme,
		dark: darkMode,
		colors,
		roundness: mergedCustomTheme.roundness ?? 4,
		palette: {
			text: {
				...customTextColors,
			},
			yellow: {
				main: darkMode ? "#FFEB3B" : "#FBC02D",
				light: darkMode ? "#FFFF72" : "rgba(240, 255, 150, 0.3)",
				dark: darkMode ? "#B2A300" : "#F57F17",
			},
			success: {
				main: darkMode ? "#4CAF50" : "#2E7D32",
				light: darkMode ? "#81C784" : "#2E7D32",
				dark: darkMode ? "#388E3C" : "#1B5E20",
				info: darkMode ? "#1B5E20" : "rgba(187, 240, 181, 1)",
			},
			error: {
				main: darkMode ? "#FF4D4D" : "#EB0014",
				light: darkMode ? "#FF9999" : "#EB0014",
				dark: darkMode ? "#CC0000" : "#C70011",
				info: darkMode ? "rgba(255, 180, 180, 1)" : "rgba(255, 220, 220, 1)",
			},
			purple: {
				main: darkMode ? "#B964F7" : "#800080",
				light: darkMode ? "#D4A4F7" : "#800080",
				dark: darkMode ? "#9932CC" : "#4B0082",
			},
			highlight: {
				main: "#C8EFFF",
				light: "#F1FF91",
			},
			chips: {
				free: "#81C784",
				premium: "#FFD700",
			},
			info: {
				main: darkMode ? "#0C4860" : "rgba(200, 239, 255, 1)",
			},
		},
		breakpoints,
		typography: customTypography,
		fonts,
		componentOverrides,
	};
};

const theme = (darkMode?: boolean, demoExamId?: DemoExamsType) =>
	buildTheme(buildType, darkMode!, demoExamId);
export default theme;
